import { useEffect, useState } from 'react';
import { Modal, Tour, Button } from 'antd';
import type { TourProps } from 'antd';

/**
 * 新手引导:首次登录弹欢迎框 → antd Tour 分步高亮左侧主流程菜单。
 * - 是否看过用 localStorage flag `onboarding_seen_<userId>` 记(换账号重新引导),不入 Redux。
 * - Header「重看引导」通过 window 事件 `onboarding:restart` 触发重跑(免 context 串联)。
 */

const RESTART_EVENT = 'onboarding:restart';

function currentUserId(): string {
  try {
    const raw = localStorage.getItem('userInfo');
    if (raw) return String(JSON.parse(raw)?.id ?? 'anon');
  } catch {
    /* ignore */
  }
  return 'anon';
}

function flagKey(): string {
  return `onboarding_seen_${currentUserId()}`;
}

/** 供外部(Header)调用:清除已看标记并重启引导。 */
export function restartOnboarding() {
  try {
    localStorage.removeItem(flagKey());
  } catch {
    /* ignore */
  }
  window.dispatchEvent(new Event(RESTART_EVENT));
}

// Tour 步骤:target 指向 Sidebar 菜单项(data-tour=路由)。菜单不存在的项 antd 会自动跳过居中显示。
const steps: TourProps['steps'] = [
  {
    title: '🤖 AI 推荐',
    description: '每天自动跑出的选股/策略推荐落在这里,是你登录后的默认首页。先看它,拿当天的候选。',
    target: () => document.querySelector('[data-tour="/app/recommendations"]') as HTMLElement,
  },
  {
    title: '🧪 因子工作台',
    description: '研究的起点:浏览因子库、看单因子表现、做条件评估。想挖掘/验证一个想法从这里开始。',
    target: () => document.querySelector('[data-tour="/app/factor-workbench"]') as HTMLElement,
  },
  {
    title: '📊 策略工作台',
    description: '核心:把因子组合成策略、跑 top-N 组合回测、一键载入「精选卡片」预填的策略再评估。',
    target: () => document.querySelector('[data-tour="/app/strategy-workbench"]') as HTMLElement,
  },
  {
    title: '💼 我的持仓',
    description: '记录你真实买入的股票,系统按持仓给出止损/风控信号。研究成果最终落到这里管理。',
    target: () => document.querySelector('[data-tour="/app/my-portfolio"]') as HTMLElement,
  },
  {
    title: '📡 监控面板',
    description: '看数据任务、调度执行、系统健康。想确认数据是否新鲜、任务是否跑完,来这里。',
    target: () => document.querySelector('[data-tour="/app/monitoring"]') as HTMLElement,
  },
];

export default function OnboardingTour() {
  const [welcomeOpen, setWelcomeOpen] = useState(false);
  const [tourOpen, setTourOpen] = useState(false);

  const markSeen = () => {
    try {
      localStorage.setItem(flagKey(), '1');
    } catch {
      /* ignore */
    }
  };

  // 首次登录(未看过)自动弹欢迎框
  useEffect(() => {
    try {
      if (!localStorage.getItem(flagKey())) setWelcomeOpen(true);
    } catch {
      /* ignore */
    }
    // 监听「重看引导」事件
    const onRestart = () => setWelcomeOpen(true);
    window.addEventListener(RESTART_EVENT, onRestart);
    return () => window.removeEventListener(RESTART_EVENT, onRestart);
  }, []);

  const startTour = () => {
    setWelcomeOpen(false);
    // 等 Modal 关闭动画结束再开 Tour,避免遮罩叠加
    setTimeout(() => setTourOpen(true), 150);
  };

  const skip = () => {
    setWelcomeOpen(false);
    markSeen();
  };

  const finishTour = () => {
    setTourOpen(false);
    markSeen();
  };

  return (
    <>
      <Modal
        open={welcomeOpen}
        onCancel={skip}
        footer={null}
        centered
        width={460}
        maskClosable={false}
      >
        <div className="text-center px-2 py-2">
          <div className="text-4xl mb-3">👋</div>
          <h2 className="text-xl font-bold text-gray-800 mb-2">欢迎来到金融Wiki量化研究平台</h2>
          <p className="text-gray-500 text-sm leading-relaxed mb-5">
            这是一个"研究 → 策略 → 持仓"一体的量化工作台。
            <br />
            花 30 秒跟着导览走一遍主流程,之后随时可在右上角头像菜单「重看引导」。
          </p>
          <div className="flex gap-3 justify-center">
            <Button onClick={skip}>跳过</Button>
            <Button type="primary" onClick={startTour}>
              开始导览
            </Button>
          </div>
        </div>
      </Modal>

      <Tour open={tourOpen} onClose={finishTour} onFinish={finishTour} steps={steps} />
    </>
  );
}
